import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "@/components/Motion";
import { Ribbon } from "@/components/Ribbon";
import { TierCard } from "@/components/TierCard";
import { BRAND, IMAGES, TIERS, CHAPTERS, SERVICES, TESTIMONIALS } from "@/lib/site";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Electrician at work" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink-900/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <SectionLabel>Advisory · Manny Diaz</SectionLabel>
        </motion.div>

        <h1 className="mt-6 font-head text-[17vw] font-medium leading-[0.82] tracking-tight text-white md:text-[13vw] lg:text-[12rem]">
          <MaskLines lines={["Get It", "Right"]} delay={0.35} />
          <span className="mask-line">
            <motion.span
              className="block text-stroke italic"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              First.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <p className="max-w-md text-base leading-relaxed text-white/70 md:text-lg">
            Independent electrical advice for homeowners, businesses and industrial teams — 40+ years of licensed expertise, guiding your project before a single wire is pulled.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-book-btn" className="group inline-flex items-center gap-3 rounded-full bg-amber px-7 py-4 font-medium uppercase tracking-[0.2em] text-black transition-transform duration-300 hover:scale-[1.04]">
              Book a Consult
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href={BRAND.phoneHref} data-testid="hero-call-btn" className="inline-flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-medium uppercase tracking-[0.2em] text-white transition-colors hover:border-amber hover:text-amber">
              <Phone className="h-4 w-4" />{BRAND.phone}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="manifesto" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
    <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr]">
      <div>
        <SectionLabel>The Approach</SectionLabel>
        <Reveal>
          <h2 className="mt-6 font-head text-5xl font-medium leading-[0.9] tracking-tight text-white md:text-7xl">
            No guesswork.<br />Just <span className="text-amber">clear</span> advice.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-md text-lg leading-relaxed text-white/60">
            Manny Diaz built his advisory practice on a simple idea: bring in a real expert <em className="text-white">before</em> the work starts, and everything downstream gets cheaper, safer and faster.
          </p>
        </Reveal>
      </div>

      <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.no} delay={i * 0.08}>
            <div className="group flex gap-6 py-8 transition-colors hover:bg-white/[0.02] md:gap-10 md:px-6">
              <span className="font-head text-5xl font-semibold text-white/15 transition-colors group-hover:text-amber md:text-6xl">{c.no}</span>
              <div>
                <h3 className="font-head text-3xl font-medium tracking-tight text-white">{c.title}</h3>
                <p className="mt-2 max-w-md text-white/55">{c.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => {
  const stats = [
    { n: "40+", l: "Years of expertise" },
    { n: "3", l: "Sectors advised" },
    { n: "1:1", l: "Direct with Manny" },
    { n: "#886996", l: "Licensed & bonded" },
  ];
  return (
    <section className="border-y border-white/10 bg-ink-800">
      <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-white/10 md:grid-cols-4">
        {stats.map((s, i) => (
          <Reveal key={s.l} delay={i * 0.06}>
            <div className="px-6 py-12 text-center md:py-16">
              <div className="font-head text-5xl font-semibold text-amber md:text-6xl">{s.n}</div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50">{s.l}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ServicesPreview = () => (
  <section data-testid="services-preview" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <SectionLabel>What We Cover</SectionLabel>
        <Reveal><h2 className="mt-6 font-head text-5xl font-medium tracking-tight text-white md:text-7xl">Expertise on tap</h2></Reveal>
      </div>
      <Link to="/services" className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.2em] text-amber hover:gap-3 transition-all">
        All services <ArrowRight className="h-4 w-4" />
      </Link>
    </div>

    <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {SERVICES.map((s, i) => (
        <Reveal key={s.title} delay={(i % 3) * 0.08}>
          <div className="group h-full rounded-2xl border border-white/10 bg-ink-700 p-8 transition-colors duration-300 hover:border-amber/40">
            <span className="font-head text-2xl font-bold text-white/15 group-hover:text-amber transition-colors">0{i + 1}</span>
            <h3 className="mt-4 font-head text-2xl font-medium tracking-tight text-white">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-white/55">{s.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const PricingPreview = () => (
  <section data-testid="pricing-preview" className="relative mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-36">
    <div className="text-center">
      <SectionLabel className="justify-center">Advisory Tiers</SectionLabel>
      <Reveal><h2 className="mt-6 font-head text-5xl font-medium tracking-tight text-white md:text-7xl">Pick your lane</h2></Reveal>
      <Reveal delay={0.08}><p className="mx-auto mt-5 max-w-xl text-white/55">Organized by audience, from the homeowner to the heavy-duty facility. Fees are reasonably priced to each project — every session ends with a clear plan.</p></Reveal>
    </div>
    <div className="mt-16 grid gap-6 lg:grid-cols-3">
      {TIERS.map((t, i) => <TierCard key={t.id} tier={t} index={i} />)}
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials" className="border-t border-white/10 bg-ink-800 px-5 py-24 md:px-10 md:py-36">
    <div className="mx-auto max-w-[1400px]">
      <SectionLabel>Trusted Locally</SectionLabel>
      <Reveal><h2 className="mt-6 font-head text-5xl font-medium tracking-tight text-white md:text-7xl">In their words</h2></Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={(i % 2) * 0.08}>
            <figure className="h-full rounded-2xl border border-white/10 bg-ink-700 p-8">
              <div className="mb-4 text-amber">{"★★★★★"}</div>
              <blockquote className="text-lg leading-relaxed text-white/75">“{t.text}”</blockquote>
              <figcaption className="mt-6 text-sm font-medium uppercase tracking-[0.2em] text-white">— {t.name}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div>
      <Hero />
      <Ribbon />
      <Manifesto />
      <Stats />
      <ServicesPreview />
      <PricingPreview />
      <Testimonials />
    </div>
  );
}
